import React, { useEffect, useState } from 'react';
import { AlertTriangle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import { Yard } from '../../../types/stock.types';
import { yardsApi } from '../../../utils/stock-api';

interface Props {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    yard: Yard | null;
    onClosed: () => void;
}

/** §15.9 — the user must type this exactly before the yard can be closed. */
const CONFIRM_PHRASE = 'CLOSE YARD';

/**
 * Close Yard (STOCK.md §9, §15.9).
 *
 * Closing is not deletion and not deactivation: the yard's orders, photos, signatures
 * and audit history all stay readable, permanently and read-only (§10). What it costs
 * is that nothing new can be raised against it.
 *
 * The typed confirmation is a deliberate-action gate. It is checked here for feedback
 * and again on the server, which is the check that actually counts.
 */
export const CloseYardDialog: React.FC<Props> = ({ open, onOpenChange, yard, onClosed }) => {
    const [reason, setReason] = useState('');
    const [confirmation, setConfirmation] = useState('');
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!open) return;
        setReason('');
        setConfirmation('');
    }, [open]);

    const phraseMatches = confirmation.trim().toUpperCase() === CONFIRM_PHRASE;
    const canSubmit = reason.trim().length > 0 && phraseMatches && !saving;

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!yard || !canSubmit) return;

        try {
            setSaving(true);
            await yardsApi.close(yard.id, reason.trim(), confirmation.trim());
            toast.success(`${yard.yardName} closed`);
            onOpenChange(false);
            onClosed();
        } catch (err: any) {
            toast.error(err?.message || 'Failed to close yard');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[520px]">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <AlertTriangle size={18} className="text-amber-500" />
                        Close {yard?.yardName}
                    </DialogTitle>
                    <DialogDescription>
                        The yard moves to Closed Yards. Its records stay fully browsable and
                        read-only — nothing is deleted. No new orders can be raised against it,
                        and it disappears from every yard selector.
                    </DialogDescription>
                </DialogHeader>

                <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="space-y-2">
                        <Label htmlFor="closeReason">Reason *</Label>
                        <Textarea
                            id="closeReason"
                            value={reason}
                            onChange={(e) => setReason(e.target.value)}
                            rows={3}
                            placeholder="Why is this yard being closed?"
                        />
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="confirmation">
                            Type <span className="font-mono font-semibold">{CONFIRM_PHRASE}</span> to confirm *
                        </Label>
                        <Input
                            id="confirmation"
                            value={confirmation}
                            onChange={(e) => setConfirmation(e.target.value)}
                            placeholder={CONFIRM_PHRASE}
                            autoComplete="off"
                        />
                        {confirmation.trim().length > 0 && !phraseMatches && (
                            <p className="text-xs text-destructive">
                                Type {CONFIRM_PHRASE} exactly to continue.
                            </p>
                        )}
                    </div>

                    <p className="text-xs text-muted-foreground">
                        A closed yard can be reopened if this was a mistake.
                    </p>

                    <DialogFooter>
                        <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                            Cancel
                        </Button>
                        <Button type="submit" variant="destructive" disabled={!canSubmit}>
                            {saving && <Loader2 size={16} className="animate-spin mr-2" />}
                            Close Yard
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
};

export default CloseYardDialog;
